import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import logo from '../assets/kaz.svg';
import craftsmanImg from '../assets/craftsman.jpg';
import clientImg    from '../assets/client.jpg';
import { useAuth } from "../context/AuthContext";

const HIDDEN_ON = [
  '/profile',
  '/craftsman/profile',
  '/admin-dashboard',
  '/shop',
];

function Navbar() {
  const [menuOpen,  setMenuOpen]  = useState(false);
  const [joinModal, setJoinModal] = useState(false);
  const { user, logout, loading } = useAuth();
  const navigate     = useNavigate();
  const { pathname } = useLocation();

  const roleRef = useRef(null);
  useEffect(() => {
    if (user?.role) roleRef.current = user.role;
  }, [user]);

  const isHidden = HIDDEN_ON.some(r => pathname === r || pathname.startsWith(r + '/'));

  useEffect(() => {
    const nav = document.querySelector('.kk-navbar');
    if (nav && !isHidden) {
      document.body.style.paddingTop = `${nav.offsetHeight}px`;
    } else {
      document.body.style.paddingTop = '0px';
    }
    return () => { document.body.style.paddingTop = ''; };
  }, [isHidden]);

  // Close the mobile menu automatically on route change
  useEffect(() => { setMenuOpen(false); }, [pathname]);

  if (isHidden) return null;
  if (loading)  return null;

  const close = () => setMenuOpen(false);

  const handleLogout = async () => {
    const role = roleRef.current;
    await logout();
    close();
    if (role === 'craftsman') {
      navigate('/login');
    } else if (role === 'client') {
      navigate('/HireLogin');
    } else {
      navigate('/');
    }
  };

  const dashLink = !user ? '/'
    : user.role === 'craftsman' ? '/craftsman-dashboard'
    : user.role === 'client'    ? '/hire'
    : '/';

  const firstName = (user?.full_name || user?.name || 'User').split(' ')[0];

  const isActive = (path) => pathname === path || (path !== '/' && pathname.startsWith(path));

  const isCraftsman = user?.role === 'craftsman';

  return (
    <>
      <style>{`
        .kk-navbar {
          background: #fff;
          border-bottom: 1px solid #eef0f2;
          padding: 14px 0;
        }
        .kk-nav-inner {
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: 24px;
          position: relative;
        }
        .kk-nav-brand {
          display: flex;
          align-items: center;
          text-decoration: none;
          flex-shrink: 0;
        }
        .kk-nav-brand img { height: 42px; width: auto; }
        .kk-nav-brand span {
          margin-left: 9px;
          font-weight: 800;
          font-size: 1.05rem;
          color: #1a1a2e;
          letter-spacing: -.01em;
        }
        .kk-nav-links {
          display: flex;
          align-items: center;
          gap: 30px;
          list-style: none;
          margin: 0;
          padding: 0;
        }
        .kk-nav-link {
          color: #4a5568;
          text-decoration: none;
          font-weight: 600;
          font-size: .95rem;
          transition: color .15s;
          padding: 6px 0;
          position: relative;
        }
        .kk-nav-link:hover { color: #198754; }
        .kk-nav-link.active { color: #198754; }
        .kk-nav-link.active::after {
          content: '';
          position: absolute;
          left: 0; right: 0; bottom: -4px;
          height: 2px;
          background: #198754;
          border-radius: 2px;
        }
        .kk-nav-right {
          display: flex;
          align-items: center;
          gap: 14px;
          flex-shrink: 0;
        }
        .kk-nav-cta {
          background: #FFD700;
          color: #1a1a2e;
          border: none;
          border-radius: 50px;
          padding: 10px 22px;
          font-weight: 700;
          font-size: .9rem;
          cursor: pointer;
          text-decoration: none;
          white-space: nowrap;
          transition: background .15s, transform .15s;
          font-family: inherit;
        }
        .kk-nav-cta:hover { background: #e6c200; transform: translateY(-1px); }
        .kk-nav-login {
          color: #1a1a2e;
          text-decoration: none;
          font-weight: 600;
          font-size: .9rem;
          white-space: nowrap;
        }
        .kk-nav-login:hover { color: #198754; }
        .kk-nav-toggle {
          display: none;
          background: none;
          border: none;
          font-size: 1.5rem;
          cursor: pointer;
          color: #1a1a2e;
          line-height: 1;
          padding: 4px 6px;
        }
        .kk-auth-greet {
          font-size: .9rem;
          font-weight: 600;
          color: #1a1a2e;
        }
        .kk-auth-logout-btn {
          background-color: #dc3545; color: white; border: none;
          border-radius: 50px; padding: 8px 20px;
          font-weight: 600; font-size: .88rem; cursor: pointer;
          font-family: inherit; transition: background .15s;
        }
        .kk-auth-logout-btn:hover { background-color: #b91c1c; }

        /* ── Menu wrapper: on desktop it's invisible to layout so its
           children (links + right) act as direct flex items of kk-nav-inner,
           exactly like before. On mobile it becomes the dropdown panel. ── */
        .kk-nav-menu { display: contents; }

        @media (max-width: 991px) {
          .kk-nav-toggle { display: block; }

          .kk-nav-menu {
            display: none;
            position: absolute; top: 100%; right: 0; left: 0;
            flex-direction: column; align-items: stretch;
            background: #fff; border-radius: 14px; border: 1px solid #eef0f2;
            box-shadow: 0 12px 32px rgba(0,0,0,.1);
            padding: 18px 20px; gap: 16px;
            z-index: 1000;
          }
          .kk-nav-menu.open { display: flex; margin-top: 8px; }

          .kk-nav-links {
            flex-direction: column;
            align-items: flex-start;
            gap: 14px;
            width: 100%;
          }
          .kk-nav-right {
            flex-direction: column;
            align-items: stretch;
            gap: 10px;
            width: 100%;
            padding-top: 14px;
            border-top: 1px solid #f1f5f9;
          }
          .kk-nav-cta, .kk-nav-login { width: 100%; text-align: center; }
        }

        .kk-join-overlay {
          position: fixed; inset: 0; background: rgba(0,0,0,.6);
          z-index: 2000; display: flex; align-items: center; justify-content: center;
          padding: 20px; animation: kk-fade-in .2s ease;
        }
        @keyframes kk-fade-in { from { opacity:0; } to { opacity:1; } }
        @keyframes kk-slide-up {
          from { opacity:0; transform: translateY(20px); }
          to   { opacity:1; transform: translateY(0); }
        }
        .kk-join-modal {
          background: #fff; border-radius: 24px;
          width: 100%; max-width: 500px; overflow: hidden;
          box-shadow: 0 40px 80px rgba(0,0,0,.3);
          font-family: inherit; animation: kk-slide-up .25s ease; position: relative;
        }
        .kk-join-close {
          position: absolute; top: 14px; right: 14px; z-index: 3;
          background: rgba(255,255,255,.92); border: 1px solid #e2e8f0;
          width: 30px; height: 30px; border-radius: 50%; cursor: pointer;
          font-size: .85rem; color: #64748b;
          display: flex; align-items: center; justify-content: center; transition: all .15s;
        }
        .kk-join-close:hover { background: #f1f5f9; color: #1e293b; }
        .kk-modal-head { padding: 24px 24px 17px; }
        .kk-modal-head .eyebrow { font-size: .68rem; font-weight: 700; letter-spacing: .1em; text-transform: uppercase; color: #16a34a; margin: 0 0 7px; }
        .kk-modal-head h2 { font-size: 1.3rem; font-weight: 900; color: #0d0d0d; margin: 0 0 4px; letter-spacing: -.02em; }
        .kk-modal-head .sub { font-size: .82rem; color: #64748b; margin: 0; }
        .kk-join-cards { display: grid; grid-template-columns: 1fr 1fr; border-top: 1.5px solid #f1f5f9; }
        .kk-join-card { display: flex; flex-direction: column; overflow: hidden; }
        .kk-join-card.craftsman { border-right: 1.5px solid #f1f5f9; }
        .kk-join-card.craftsman:hover .kk-join-card-body { background: #fefce8; }
        .kk-join-card.client:hover .kk-join-card-body { background: #f0fdf4; }
        .kk-join-card-banner {
          position: relative; height: 150px; overflow: hidden; display: flex;
          align-items: center; justify-content: center;
        }
        .kk-join-card.craftsman .kk-join-card-banner { background: #1a1a2e; }
        .kk-join-card.client    .kk-join-card-banner { background: #0d2e18; }
        .kk-join-card-banner img {
          width: 100%; height: 100%; object-fit: cover; object-position: center top;
          display: block;
        }
        .kk-join-card-banner-label {
          position: absolute; bottom: 0; left: 0; right: 0;
          padding: 28px 14px 10px;
          background: linear-gradient(to top, rgba(0,0,0,.65), transparent);
          font-size: .68rem; font-weight: 700; letter-spacing: .08em; text-transform: uppercase;
        }
        .kk-join-card.craftsman .kk-join-card-banner-label { color: #FFD700; }
        .kk-join-card.client    .kk-join-card-banner-label { color: #86efac; }
        .kk-join-card-body { padding: 15px 15px 17px; display: flex; flex-direction: column; flex: 1; transition: background .15s; }
        .kk-join-card-title { font-size: .9rem; font-weight: 800; color: #0d0d0d; margin: 0 0 5px; }
        .kk-join-card-desc { font-size: .72rem; color: #64748b; margin: 0 0 13px; line-height: 1.55; flex: 1; font-weight: 500; }
        .kk-join-card-links { display: flex; flex-direction: column; gap: 7px; }
        .kk-join-card-signup {
          display: inline-flex; align-items: center; gap: 5px;
          font-size: .78rem; font-weight: 700; text-decoration: none;
        }
        .kk-join-card.craftsman .kk-join-card-signup { color: #ca8a04; }
        .kk-join-card.client    .kk-join-card-signup { color: #16a34a; }
        .kk-join-card-login { font-size: .69rem; color: #94a3b8; text-decoration: none; }
        .kk-join-card-login span { color: #0d0d0d; font-weight: 600; }
        .kk-join-card.craftsman .kk-join-card-login span { border-bottom: 1.5px solid #ffc107; }
        .kk-join-card.client    .kk-join-card-login span { border-bottom: 1.5px solid #16a34a; }
        .kk-join-login {
          text-align: center; font-size: .77rem; color: #94a3b8;
          padding: 12px 24px; border-top: 1px solid #f1f5f9; margin: 0;
        }
        .kk-join-login a { color: #0d0d0d; font-weight: 800; text-decoration: none; border-bottom: 2px solid #FFD700; padding-bottom: 1px; }
        .kk-join-login a:hover { color: #e6c200; }
      `}</style>

      <nav className="kk-navbar fixed-top">
        <div className="container kk-nav-inner">
          <Link className="kk-nav-brand" to={dashLink} onClick={close}>
            <img src={logo} alt="KaaKazini Logo"/>
            <span>KAAKAZINI</span>
          </Link>

          <button
            className="kk-nav-toggle"
            onClick={() => setMenuOpen(v => !v)}
            aria-label="Toggle menu"
            aria-expanded={menuOpen}
          >
            {menuOpen ? '✕' : '☰'}
          </button>

          <div className={`kk-nav-menu ${menuOpen ? 'open' : ''}`}>
            {!user && (
              <ul className="kk-nav-links">
                <li><Link className={`kk-nav-link ${isActive('/') && pathname === '/' ? 'active' : ''}`} to="/" onClick={close}>Home</Link></li>
                <li><Link className={`kk-nav-link ${isActive('/services') ? 'active' : ''}`} to="/services" onClick={close}>Services</Link></li>
                <li><Link className={`kk-nav-link ${isActive('/craftsmen') ? 'active' : ''}`} to="/craftsmen" onClick={close}>Providers</Link></li>
                <li><Link className={`kk-nav-link ${isActive('/jobs') ? 'active' : ''}`} to="/jobs" onClick={close}>Jobs</Link></li>
                <li><Link className={`kk-nav-link ${isActive('/signup') ? 'active' : ''}`} to="/signup" onClick={close}>Become a Provider</Link></li>
                {/* Shop */}
                <li>
                  <a
                    href="https://shop.kaakazini.com"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="kk-nav-link"
                    onClick={close}
                  >
                    Shop
                  </a>
                </li>
              </ul>
            )}

            <div className="kk-nav-right">
              {user ? (
                <>
                  <span className="kk-auth-greet">Hi, {firstName}</span>
                  <button className="kk-auth-logout-btn" onClick={handleLogout}>Logout</button>
                </>
              ) : (
                <>
                  <Link className="kk-nav-login" to="/login" onClick={close}>Login</Link>
                  <button
                    className="kk-nav-cta"
                    onClick={() => { close(); setJoinModal(true); }}
                  >
                    Join Now
                  </button>
                </>
              )}
            </div>
          </div>
        </div>
      </nav>

      {joinModal && (
        <div className="kk-join-overlay" onClick={() => setJoinModal(false)}>
          <div className="kk-join-modal" onClick={e => e.stopPropagation()}>
            <button className="kk-join-close" onClick={() => setJoinModal(false)}>✕</button>

            <div className="kk-modal-head">
              <p className="eyebrow">KaaKazini</p>
              <h2>Join the platform</h2>
              <p className="sub">Who are you here as?</p>
            </div>

            <div className="kk-join-cards">
              <div className="kk-join-card craftsman">
                <div className="kk-join-card-banner">
                  <img src={craftsmanImg} alt="Service Provider"/>
                  <span className="kk-join-card-banner-label">Service Provider</span>
                </div>
                <div className="kk-join-card-body">
                  <p className="kk-join-card-title">I'm a Service Provider</p>
                  <p className="kk-join-card-desc">Create a profile, showcase your work, and receive job requests</p>
                  <div className="kk-join-card-links">
                    <Link to="/signup" className="kk-join-card-signup" onClick={() => setJoinModal(false)}>
                      Sign up
                      <svg width="12" height="12" viewBox="0 0 12 12" fill="none"><path d="M2 6h8M7 3l3 3-3 3" stroke="#ca8a04" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/></svg>
                    </Link>
                    <Link to="/login" className="kk-join-card-login" onClick={() => setJoinModal(false)}>
                      Already have an account? <span>Log in</span>
                    </Link>
                  </div>
                </div>
              </div>

              <div className="kk-join-card client">
                <div className="kk-join-card-banner">
                  <img src={clientImg} alt="Client"/>
                  <span className="kk-join-card-banner-label">Client</span>
                </div>
                <div className="kk-join-card-body">
                  <p className="kk-join-card-title">I'm a Client</p>
                  <p className="kk-join-card-desc">Find and hire skilled service providers for your home or business</p>
                  <div className="kk-join-card-links">
                    <Link to="/HireSignUp" className="kk-join-card-signup" onClick={() => setJoinModal(false)}>
                      Sign up
                      <svg width="12" height="12" viewBox="0 0 12 12" fill="none"><path d="M2 6h8M7 3l3 3-3 3" stroke="#16a34a" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/></svg>
                    </Link>
                    <Link to="/HireLogin" className="kk-join-card-login" onClick={() => setJoinModal(false)}>
                      Already have an account? <span>Log in</span>
                    </Link>
                  </div>
                </div>
              </div>
            </div>

            <p className="kk-join-login">
              Provider login? <Link to="/login" onClick={() => setJoinModal(false)}>Log in here</Link>
            </p>
          </div>
        </div>
      )}
    </>
  );
}

export default Navbar;
